import Image from "next/image";

import { Link } from "@/components/link/link.component";
import { RichText } from "@/components/richText/richText.component";

import { portableTextToPlain } from "@/utils/portableText.util";
import { resolveLinkHref } from "@/utils/url.util";

import type { GetLocalPageQueryResult } from "@/sanity.types";

export const AdtActionCard = ({ block }: { block: AdtActionCardBlock }) => {
  const titleText = portableTextToPlain(block.title);
  if (!titleText) return null;

  const imageUrl = block.image?.url;
  const href = resolveLinkHref(block.ctaLink);
  const showCta = Boolean(block.ctaLabel && href && href !== "#");
  const descriptionText = portableTextToPlain(block.description);

  return (
    <section
      data-testid="adt-action-card"
      aria-labelledby={`adt-action-card-title-${block._key}`}
      className="w-full px-5 py-6 sm:px-8 sm:py-8">
      <div
        data-testid="adt-action-card-layout"
        className="mx-auto flex w-full max-w-336 flex-col items-stretch gap-6 rounded-xl border border-[#e5e7eb] bg-white px-5 py-8 sm:flex-row sm:items-center sm:gap-8 sm:p-10">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={block.image?.alt || titleText}
            width={574}
            height={322}
            sizes="(max-width: 639px) 100vw, 287px"
            className="aspect-287/161 h-auto w-full shrink-0 rounded-lg object-cover sm:h-40.25 sm:w-71.75"
          />
        ) : null}

        <div
          data-testid="adt-action-card-copy"
          className="flex min-w-0 flex-1 flex-col gap-6 text-left">
          <div
            id={`adt-action-card-title-${block._key}`}
            className="text-greyscale-headline">
            <RichText>{block.title}</RichText>
          </div>
          {descriptionText ? (
            <div className="text-greyscale-body">
              <RichText>{block.description}</RichText>
            </div>
          ) : null}
        </div>

        {showCta ? (
          <Link
            href={href}
            secondary
            border
            target={block.ctaLink?.openInNewTab ? "_blank" : undefined}
            rel={
              block.ctaLink?.openInNewTab ? "noopener noreferrer" : undefined
            }
            className="border-adt-blue! text-adt-blue! w-full shrink-0 px-8 py-3 whitespace-nowrap sm:w-auto">
            {block.ctaLabel}
          </Link>
        ) : null}
      </div>
    </section>
  );
};

type AdtActionCardBlock = Extract<
  NonNullable<NonNullable<GetLocalPageQueryResult>["pageBuilder"]>[number],
  { _type: "adtActionCard" }
>;
